import React from 'react';
import mapboxgl from 'mapbox-gl';
import { Dialog } from 'primereact/dialog';
import { Slider } from 'primereact/slider';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { toast } from 'react-toastify';

const MAX_POLYGONS = 3;
// Limite da Isochrone API do Mapbox: contours_minutes aceita no máximo 60 minutos
// (o modal do mapa Google, via Google DistanceMatrix, permitia 180).
const MAX_MINUTES = 60;

// Perfis da Isochrone API equivalentes aos modos usados no mapa Google.
const PROFILES = { driving: 'driving', bicycling: 'cycling', walking: 'walking' };
const LABELS = { driving: 'De Carro', bicycling: 'De Bicicleta', walking: 'A Pé' };

const CORES_OPTIONS = [
    { name: '#0e53ce', value: '#0e53ce' },
    { name: '#095609', value: '#095609' },
    { name: '#5d3d2a', value: '#5d3d2a' },
    { name: '#DC143C', value: '#DC143C' },
    { name: '#6007a9', value: '#6007a9' },
    { name: '#bd0f72', value: '#bd0f72' },
];

/**
 * Versão exclusiva do MapBox do components/RaioPorDeslocamento/ModalRaioPorDeslocamento.jsx
 * (que segue intacto para o mapa Google). Em vez do Google DistanceMatrix iterativo,
 * usa a Isochrone API do Mapbox: um único GET devolve os polígonos em GeoJSON.
 *
 * Props:
 *  - show, onHide
 *  - center: {lat, lng} — ponto de partida do deslocamento
 *  - onIsochronesReady(isochrones, tipoLabel): [{feature, tempo, cor}] em ordem crescente de tempo
 */
class RaioDeslocamentoModal extends React.Component {
    state = {
        tipo_deslocamento: 'driving',
        loading: false,
        configOptions: [{ tempo: 0, cor: null }],
    };

    justifyOptions = [
        { icon: 'fa fa-car', value: 'driving', title: 'De Carro' },
        { icon: 'fa fa-bicycle', value: 'bicycling', title: 'De Bicicleta' },
        { icon: 'fa fa-person-walking', value: 'walking', title: 'A Pé' },
    ];

    justifyTemplate(option) {
        return <i title={option.title} className={option.icon}></i>;
    }

    colorTemplate(option) {
        return (
            <div style={{ backgroundColor: option.value }}>
                <div>&nbsp;</div>
            </div>
        );
    }

    selectedOptionTemplate(option, props) {
        if (option) {
            return (
                <div style={{ backgroundColor: option.value }}>
                    <div>&nbsp;</div>
                </div>
            );
        }
        return <span>{props.placeholder}</span>;
    }

    handleTempoChanged = ({ value, index }) => {
        const options = [...this.state.configOptions];
        options[index] = { ...options[index], tempo: value };
        this.setState({ configOptions: options });
    };

    handleColorChanged = ({ value, index }) => {
        const options = [...this.state.configOptions];
        options[index] = { ...options[index], cor: value };
        this.setState({ configOptions: options });
    };

    handleSubmit = async () => {
        const { center } = this.props;
        const { tipo_deslocamento, configOptions } = this.state;

        // Cor default cicla a paleta quando o usuário não escolhe.
        const options = configOptions
            .map((o, i) => ({
                tempo: Math.round(o.tempo || 0),
                cor: o.cor || CORES_OPTIONS[i % CORES_OPTIONS.length].value,
            }))
            .filter((o) => o.tempo > 0);

        if (!center || options.length === 0) {
            toast.warn('Informe ao menos um tempo de deslocamento.');
            return;
        }

        // A API aceita até 4 contornos por request, em minutos distintos e crescentes.
        const byTempo = {};
        options.forEach((o) => { byTempo[o.tempo] = o; });
        const minutes = Object.keys(byTempo).map(Number).sort((a, b) => a - b);

        this.setState({ loading: true });
        try {
            const profile = PROFILES[tipo_deslocamento];
            const url = `https://api.mapbox.com/isochrone/v1/mapbox/${profile}/${center.lng},${center.lat}` +
                `?contours_minutes=${minutes.join(',')}&polygons=true&denoise=1&access_token=${mapboxgl.accessToken}`;

            // fetch puro: o axios global carrega headers do app (CSRF/auth) que não
            // passam no CORS da API do Mapbox.
            const res = await fetch(url);
            if (!res.ok) throw new Error(`Isochrone API HTTP ${res.status}`);
            const geojson = await res.json();

            const isochrones = (geojson.features || [])
                .filter((f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon')
                .map((f) => {
                    const option = byTempo[f.properties?.contour];
                    return option ? { feature: f, tempo: option.tempo, cor: option.cor } : null;
                })
                .filter(Boolean)
                .sort((a, b) => a.tempo - b.tempo);

            if (isochrones.length === 0) {
                toast.warn('Não foi possível calcular o deslocamento para este ponto.');
                return;
            }

            this.props.onHide();
            await this.props.onIsochronesReady(isochrones, LABELS[tipo_deslocamento]);
        } catch (e) {
            console.error('[RaioDeslocamentoModal] erro na Isochrone API:', e);
            toast.error('Erro ao calcular o raio por deslocamento.');
        } finally {
            this.setState({ loading: false });
        }
    };

    render() {
        return (
            <Dialog
                header="Mapa Por Tempo de Deslocamento"
                visible={this.props.show}
                style={{ width: '35vw', minWidth: 420, marginTop: '50px' }}
                onHide={this.props.onHide}
                blockScroll={true}
            >
                <div className="form-group">
                    <SelectButton
                        value={this.state.tipo_deslocamento}
                        options={this.justifyOptions}
                        onChange={(e) => e.value && this.setState({ tipo_deslocamento: e.value })}
                        itemTemplate={this.justifyTemplate}
                    />
                </div>

                {this.state.configOptions.map((option, index) => {
                    const { tempo, cor } = option;
                    return (
                        <React.Fragment key={index}>
                            <div className="form-group row">
                                <div className="col-5">
                                    {index === 0 && <label htmlFor="">Tempo de Deslocamento (Em minutos):</label>}
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={MAX_MINUTES}
                                        value={tempo}
                                        onChange={(e) => this.handleTempoChanged({ value: e.value, index })}
                                    />
                                    <Slider
                                        value={tempo}
                                        min={0}
                                        max={MAX_MINUTES}
                                        onChange={(e) => this.handleTempoChanged({ value: e.value, index })}
                                    />
                                </div>
                                <div className="col-5">
                                    {index === 0 && <label htmlFor="raio">Cor do Contorno</label>}
                                    <Dropdown
                                        style={{ width: '100%' }}
                                        value={cor}
                                        options={CORES_OPTIONS}
                                        optionLabel={'name'}
                                        onChange={(e) => this.handleColorChanged({ value: e.value, index })}
                                        itemTemplate={this.colorTemplate}
                                        valueTemplate={this.selectedOptionTemplate}
                                        placeholder="Selecione a Cor"
                                    />
                                </div>

                                <div className="col-2">
                                    {index > 0 && (
                                        <>
                                            <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() => {
                                                    const options = [...this.state.configOptions];
                                                    options.splice(index, 1);
                                                    this.setState({ configOptions: options });
                                                }}
                                                title={'Remover'}
                                            >
                                                <i className="fa fa-trash" />
                                            </button>
                                            &nbsp;
                                        </>
                                    )}
                                    {this.state.configOptions.length < MAX_POLYGONS && (
                                        <button
                                            className="btn btn-sm btn-success"
                                            onClick={() =>
                                                this.setState({
                                                    configOptions: [...this.state.configOptions, { tempo: 0, cor: null }],
                                                })
                                            }
                                            title={'Adicionar'}
                                        >
                                            <i className="fa fa-plus" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}

                <small className="text-muted d-block mb-2">
                    Tempo máximo de {MAX_MINUTES} minutos por contorno.
                </small>

                <div className="form-group">
                    <button
                        className="btn btn-default btn-sm btn-block"
                        disabled={this.state.loading}
                        onClick={this.handleSubmit}
                    >
                        {this.state.loading && <i className="fa fa-spin fa-spinner" />} Desenhar
                    </button>
                </div>
            </Dialog>
        );
    }
}

export default RaioDeslocamentoModal;
